import { createContext, ReactNode, use } from "react";
import { useInView } from "react-intersection-observer";

import { ScriptLoadingStates } from "@/utils/hooks/useScript.hook";
import "@/utils/validation";

import { useNeverbounceScript } from "./neverbounceInitializer";

type FormInfoContextValues = {
    formId?: string;
};

const FormInfoContext = createContext<FormInfoContextValues>({
    formId: undefined
});

FormInfoContext.displayName = "MicroCopyContext";

type FormInfoProviderProps = {
    children: ({ initialized }: { initialized: boolean }) => ReactNode;
    formId: string;
    withNeverbounce: boolean;
};

const FormInfoProvider = ({
    children,
    formId,
    withNeverbounce
}: FormInfoProviderProps) => {
    const { ref, inView } = useInView({
        triggerOnce: true,
        rootMargin: "400px 0px 0px 0px"
    });

    const status = useNeverbounceScript({
        disabled: !withNeverbounce || !inView
    });

    const initialized =
        withNeverbounce &&
        (status === ScriptLoadingStates.Error ||
            status === ScriptLoadingStates.Ready);

    return (
        <div ref={ref}>
            <FormInfoContext
                value={{
                    formId
                }}
            >
                {children({ initialized: initialized })}
            </FormInfoContext>
        </div>
    );
};

const useFormInfoContext = (): FormInfoContextValues => {
    const context = use<FormInfoContextValues>(FormInfoContext);
    if (context === undefined) {
        throw new Error(
            "useFormInfoContext can only be used inside FormInfoProvider"
        );
    }
    return context;
};

export { FormInfoProvider, useFormInfoContext };
